import { AlertCircle, Stethoscope } from "lucide-react";

import { PRODUCT_NAME } from "@src/shared/brand";
import { HeaderText } from "@src/shared/components/general/HeaderText";
import { Text } from "@src/shared/components/general/Text";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import { Button } from "@src/shared/components/ui/button";
import { Spinner } from "@src/shared/components/ui/spinner";

export interface IframeLoginPageV2Props {
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

export function IframeLoginPageV2({
  isLoading,
  error,
  onRetry,
}: IframeLoginPageV2Props) {
  return (
    <VerticalFlexbox
      as="main"
      alignItems="center"
      justifyContent="center"
      className="min-h-full w-full bg-background px-6 py-10 text-foreground"
    >
      <VerticalFlexbox
        alignItems="center"
        className="w-full max-w-[360px] text-center"
      >
        <div className="flex size-[46px] items-center justify-center rounded-[13px] bg-accent text-primary">
          <Stethoscope
            className="size-[22px]"
            strokeWidth={2}
            aria-hidden="true"
          />
        </div>

        <Text
          as="p"
          appearance="subdued"
          className="mt-4 font-mono text-[10.5px] font-semibold uppercase tracking-[0.28em]"
        >
          {PRODUCT_NAME.singular}
        </Text>

        <HeaderText
          tag="h1"
          className="mt-[11px] text-balance text-[24px] leading-tight tracking-[-0.6px]"
        >
          Connecting to your chart
        </HeaderText>

        {isLoading ? (
          <VerticalFlexbox alignItems="center" className="mt-6" gap="md">
            <Spinner size="lg" />
            <Text appearance="subdued" sizing="sm">
              Signing you in from Elation...
            </Text>
          </VerticalFlexbox>
        ) : null}

        {error && !isLoading ? (
          <VerticalFlexbox
            alignItems="center"
            className="mt-6 w-full rounded-xl border border-destructive/40 bg-destructive/10 px-4 py-4"
            gap="sm"
            role="alert"
          >
            <AlertCircle
              className="size-5 text-destructive"
              strokeWidth={2}
              aria-hidden="true"
            />
            <Text className="font-semibold" sizing="sm">
              Something went wrong
            </Text>
            <Text appearance="subdued" sizing="sm">
              {error}
            </Text>
            {onRetry ? (
              <Button
                className="mt-2 h-9 rounded-[9px] px-4 text-[13.5px] font-semibold"
                onClick={onRetry}
                type="button"
                variant="outline"
              >
                Refresh
              </Button>
            ) : null}
          </VerticalFlexbox>
        ) : null}
      </VerticalFlexbox>
    </VerticalFlexbox>
  );
}
